import axios from 'axios';
import { API } from '../contexts/AuthContext';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  mobile?: string;
  businessName: string;
  role: string;
  emailVerified: boolean;
  lockedUntil?: string;
  blockedAt?: string;
  createdAt: string;
  balance: string;
  generationCount: number;
  lastGenerationAt?: string;
}

export interface AdminCreditTransaction {
  id: string;
  delta: string;
  reason: string;
  referenceId?: string;
  balanceAfter: string;
  actorId?: string;
  metadata?: Record<string, unknown>;
  note?: string;
  createdAt: string;
}

export type GenerationStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export function generationStatusPillClass(status: GenerationStatus): 'success' | 'failed' | 'pending' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'failed';
  return 'pending';
}

export function formatMicroUsd(value: string): string {
  return value === '0' ? '—' : `$${(Number(value) / 1_000_000).toFixed(6)}`;
}

export interface AdminGeneration {
  id: string;
  status: GenerationStatus;
  aiProvider: string;
  createdAt: string;
  costMicroUsd: string;
}

export interface AdminUserDetail extends AdminUser {
  creditTransactions: AdminCreditTransaction[];
  generations: AdminGeneration[];
  oauthAccounts: { provider: string; email?: string }[];
}

export interface AdminPayment {
  id: string;
  userId: string;
  email: string;
  businessName: string;
  delta: string;
  referenceId?: string;
  balanceAfter: string;
  createdAt: string;
}

export interface AdminGenerationRow {
  id: string;
  status: GenerationStatus;
  aiProvider: string;
  model?: string;
  createdAt: string;
  userId: string;
  userEmail: string;
  totalTokens: number;
  costMicroUsd: string;
  tokenBreakdown?: Record<string, unknown>;
}

export interface AdminGenerationTokenUsage {
  phase: string;
  model: string;
  promptTokens: number;
  candidateTokens: number;
  totalTokens: number;
  costMicroUsd: string;
}

export interface AdminGenerationDetail {
  id: string;
  status: GenerationStatus;
  aiProvider: string;
  createdAt: string;
  errorMessage?: string;
  productDescription: string;
  userInstructions?: string;
  generatedPrompt: string;
  roomImageUrl: string | null;
  productImageUrl: string | null;
  referenceImageUrls: string[];
  resultImageUrl: string | null;
  userId: string;
  userEmail: string;
  businessName: string;
  totalTokens: number;
  costMicroUsd: string;
  tokenUsage: AdminGenerationTokenUsage[];
}

export interface OverviewData {
  totalUsers: number;
  newUsers7d: number;
  newUsers30d: number;
  guestSessionsStarted7d: number;
  guestSessionsStarted30d: number;
  guestConverted7d: number;
  guestConverted30d: number;
  guestConversionRate30d: number;
  activeUsers7d: number;
  activeUsers30d: number;
  creditsOutstanding: string;
  creditsIssued30d: string;
  creditsConsumed30d: string;
  generations: {
    total: number;
    succeeded: number;
    failed: number;
    successRate: number;
    last7d: number;
  };
  modelUsage: Array<{
    model: string;
    generations: number;
    totalTokens: number;
    totalCostMicroUsd: string;
  }>;
  phaseUsage: Array<{
    phase: string;
    calls: number;
    totalTokens: number;
    totalCostMicroUsd: string;
  }>;
  signupsByDay: Array<{ date: string; count: number }>;
  generationsByDay: Array<{ date: string; succeeded: number; failed: number }>;
  creditsByDay: Array<{ date: string; issued: string; consumed: string }>;
  revenueByDay: Array<{ date: string; microUsd: string }>;
}

// ── Query param types ─────────────────────────────────────────────────────────

export interface UsersParams {
  search?: string;
  role?: string;
  verified?: string;
  locked?: string;
  blocked?: string;
  hasGenerations?: string;
  createdFrom?: string;
  createdTo?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}

export interface PaymentsParams {
  userId?: string;
  search?: string;
  from?: string;
  to?: string;
  minAmount?: string;
  maxAmount?: string;
  limit?: number;
  cursor?: string;
}

export interface GenerationsParams {
  status?: string;
  model?: string;
  provider?: string;
  userId?: string;
  search?: string;
  from?: string;
  to?: string;
  minCost?: string;
  maxCost?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function getOverview(from?: string, to?: string): Promise<OverviewData> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await axios.get<OverviewData>(`${API}/admin/overview`, { params });
  return res.data;
}

export async function getUsers(
  params: UsersParams,
): Promise<{ users: AdminUser[]; nextCursor: string | null }> {
  const res = await axios.get(`${API}/admin/users`, { params });
  return res.data;
}

export async function getUserDetail(id: string): Promise<AdminUserDetail> {
  // Backend returns { user, transactions, generations, oauthProviders } — flatten to AdminUserDetail shape.
  const res = await axios.get<{ user: AdminUser; transactions: AdminCreditTransaction[]; generations: AdminGeneration[]; oauthProviders: { provider: string; email?: string }[] }>(`${API}/admin/users/${id}`);
  const { user, transactions, generations, oauthProviders } = res.data;
  return {
    ...user,
    creditTransactions: transactions,
    generations,
    oauthAccounts: oauthProviders,
  };
}

export async function getPayments(
  params: PaymentsParams,
): Promise<{ payments: AdminPayment[]; nextCursor: string | null }> {
  const res = await axios.get(`${API}/admin/payments`, { params });
  return res.data;
}

export async function getGenerations(
  params: GenerationsParams,
): Promise<{ generations: AdminGenerationRow[]; nextCursor: string | null }> {
  const res = await axios.get(`${API}/admin/generations`, { params });
  return res.data;
}

export async function getGenerationDetail(id: string): Promise<AdminGenerationDetail> {
  const res = await axios.get<AdminGenerationDetail>(`${API}/admin/generations/${id}`);
  return res.data;
}

export async function adjustCredits(
  userId: string,
  delta: string,
  note: string,
): Promise<{ newBalance: string }> {
  const res = await axios.post<{ newBalance: string }>(
    `${API}/admin/users/${userId}/credits`,
    { delta, note },
  );
  return res.data;
}

export async function unlockUser(userId: string): Promise<void> {
  await axios.post(`${API}/admin/users/${userId}/unlock`);
}

export async function verifyUserEmail(userId: string): Promise<void> {
  await axios.post(`${API}/admin/users/${userId}/verify-email`);
}

export async function blockUser(userId: string, reason: string): Promise<void> {
  await axios.post(`${API}/admin/users/${userId}/block`, { reason });
}

export async function unblockUser(userId: string): Promise<void> {
  await axios.post(`${API}/admin/users/${userId}/unblock`);
}
